import { Controller } from "@hotwired/stimulus";
import { loadStripe } from "@stripe/stripe-js";
import { errorHandlers } from "../mixins/errorHandlers";

export default class extends Controller {
  static values = {
    publishableKey: String,
    clientSecret: String,
    returnUrl: String,
    cardHolderNameRequiredMessage: String,
  };

  static targets = ["card", "form", "cardHolderName", "cardHolderNameError", "error", "submit"];

  async connect() {
    errorHandlers(this);
    this.submitTarget.disabled = true;
    // Reuse the page's fullscreen loading overlay (the same one shown on initial page load) to
    // block interaction while we await Stripe/the server. It lives outside our form scope and is
    // only present on the FW-branded payment page, so guard for its absence.
    this.fullscreenSpinner = document.querySelector(".spinner-fullscreen");

    try {
      this.stripe = await loadStripe(this.publishableKeyValue);
      this.elements = this.stripe.elements({
        clientSecret: this.clientSecretValue,
        appearance: {
          theme: "stripe",
        },
      });
      this.paymentElement = this.elements.create("payment", {
        fields: { billingDetails: { name: "never" } },
        layout: { type: "tabs" },
      });
      // Clear the top-of-form banner (server/Stripe errors) when the user starts editing the
      // Element, so a stale message from a previous submit doesn't linger. We key off 'focus'
      // rather than 'change' on purpose: confirmSetup failures make Stripe render its own inline
      // field error, which fires a programmatic 'change' - clearing on that would wipe the server
      // error banner we just set (it would flash and vanish). 'focus' only fires on user intent.
      this.paymentElement.on("focus", () => this.clearFormError());
      this.paymentElement.mount(this.cardTarget);
      this.submitTarget.disabled = false;
    } catch (error) {
      this.appendSupportAlertMessage(error.message || error, this.errorTarget);
    }
  }

  async submit(event) {
    event.preventDefault();
    if (!this.stripe || !this.elements) return;

    this.clearFormError();

    const cardHolderName = this.hasCardHolderNameTarget ? this.cardHolderNameTarget.value.trim() : "";
    // Bupa's Orders API requires the cardholder name. Stripe's Payment Element never renders a
    // name field for card payments, so this is the only place we can enforce its presence.
    if (this.hasCardHolderNameTarget && !cardHolderName) {
      this.showCardHolderNameError(this.cardHolderNameRequiredMessageValue);
      this.cardHolderNameTarget.focus();
      return;
    }

    this.setLoading(true);

    const returnUrl = new URL(this.returnUrlValue);
    if (this.hasCardHolderNameTarget) {
      returnUrl.searchParams.set("card_holder_name", cardHolderName);
    }

    // redirect: 'if_required' only sends the browser away for redirect-based next actions
    // (eg. 3DS). Otherwise it resolves here with the confirmed setupIntent, which is the
    // only place payment_method is available - Stripe's redirect never includes it.
    const { error, setupIntent } = await this.stripe.confirmSetup({
      elements: this.elements,
      confirmParams: {
        return_url: returnUrl.toString(),
        // Sets the cardholder name on the PaymentMethod's billing_details.name.
        payment_method_data: {
          billing_details: { name: cardHolderName },
        },
      },
      redirect: "if_required",
    });

    if (error) {
      this.setLoading(false);
      this.appendSupportAlertMessage(error.message, this.errorTarget);
      return;
    }

    returnUrl.searchParams.set("setup_intent", setupIntent.id);
    returnUrl.searchParams.set("redirect_status", setupIntent.status);
    returnUrl.searchParams.set("payment_method", setupIntent.payment_method);
    window.location.href = returnUrl.toString();
  }

  clearCardHolderNameError() {
    if (!this.hasCardHolderNameTarget) return;

    this.cardHolderNameErrorTarget.textContent = "";
    this.cardHolderNameTarget.classList.remove("stripe-field-input--invalid");
  }

  showCardHolderNameError(message) {
    if (!this.hasCardHolderNameTarget) return;

    this.cardHolderNameErrorTarget.textContent = message;
    // Red border on the field itself, matching how Stripe marks an invalid Element field.
    this.cardHolderNameTarget.classList.add("stripe-field-input--invalid");
  }

  // Clears the top-of-form banner used for server/Stripe errors. appendSupportAlertMessage only
  // appends, so we also strip the alert classes it adds to keep the empty container invisible.
  clearFormError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = "";
      this.errorTarget.classList.remove("alert", "alert-danger");
    }
  }

  // Locks the form and shows the fullscreen overlay while we await Stripe/the server, so the
  // user can't edit fields or resubmit mid-request. The overlay covers the whole page (including
  // the Stripe iframe); disabling the inputs/Element is a belt-and-suspenders guard beneath it.
  setLoading(isLoading) {
    this.submitTarget.disabled = isLoading;
    if (this.hasCardHolderNameTarget) {
      this.cardHolderNameTarget.disabled = isLoading;
    }
    if (this.paymentElement) {
      this.paymentElement.update({ readOnly: isLoading });
    }
    if (this.fullscreenSpinner) {
      this.fullscreenSpinner.style.display = isLoading ? "block" : "none";
    }
  }
}
